<template>
	<!-- Simple header for create mode (no trip exists) -->
	<div v-if="mode == 'create'" class="ReportTitle">
		<CloHeading id="content" :level="headingLevel.toString()" :reverse="reverse">
			{{ $t('tripReport.create') }}
		</CloHeading>
	</div>

	<!-- Full header when trip exists -->
	<div v-else class="ReportTitle" :class="classes">
		<!-- -------- Context -------- -->
		<!-- Create mode -->
		<div v-if="mode == 'create'" class="ReportTitle-context">
			<span>{{ $t('tripReport.create') }}</span>
		</div>
		<!-- Edit mode -->
		<div v-else-if="mode == 'edit'" class="ReportTitle-context">
			<span>{{ $t('tripReport.edit') }} {{ tripId }}</span>
		</div>
		<!-- Default -->
		<!-- <div v-else class="ReportTitle-context is-visuallyHidden">
			<span>{{ $t('tripReport.title') }} {{ tripId }}</span>
		</div> -->

		<!-- -------- Trip name -------- -->
		<div class="ReportTitle-name">
			<CloHeading :id="list ? tripId : 'content'" :level="headingLevel.toString()" :size="headingSize.toString()" :reverse="reverse">
				<a :href="`${baseUrl}tripreport/${tripId}`" v-if="list">{{ tripName }}</a>
				<span v-else>{{ tripName }}</span>
			</CloHeading>
		</div>

		<!-- -------- Trip dates -------- -->
		<!-- For single day trip reports -->
		<div v-if="duration == 1" class="ReportTitle-time">
			<time :title="`${localizeDate(tripBeginDt)}—${localizeDate(tripEndDt)}`" :datetime="tripBeginDt">{{ beginDateFormatted }}</time>
		</div>
		<!-- For multi-day trip reports -->
		<div v-else class="ReportTitle-time">
			<!-- Begin date -->
			<time :title="localizeDate(tripBeginDt)" :datetime="tripBeginDt">
				<span>{{ beginDateFormatted }}</span>
			</time>
			<!-- Dash -->
			<span>&ndash;</span>
			<!-- End date -->
			<time :title="localizeDate(tripEndDt)" :datetime="tripEndDt">
				<span>{{ endDateFormatted }}</span>
			</time>
			<!-- Duration -->
			<!-- <time v-if="duration > 1" :datetime="'P'+duration+'D'">({{ duration }} days)</time> -->
			<time v-if="duration > 1" :datetime="'P' + duration + 'D'">({{ $t('tripReport.numberDays', [duration]) }})</time>
		</div>

		<!-- -------- Visibility -------- -->
		<div class="ReportTitle-privacy">
			<span class="is-visuallyHidden">{{ $t('tripReport.visibility') }}</span>
			<!-- privacySetting can be 'locked', 'link', or 'open' -->
			<span v-if="privacySetting === 'link'">{{ $t('tripReport.visibilityLinkOnly') }}</span>
			<span v-else-if="privacySetting === 'open'">{{ $t('tripReport.visibilityPublic') }}</span>
			<span v-else-if="privacySetting === 'locked'">{{ $t('tripReport.visibilityLimited') }}</span>
		</div>
	</div>
</template>

<script>
import CloHeading from '@/base/CloHeading/CloHeading.vue';
export default {
	name: 'ReportTitle',
	components: {
		CloHeading,
	},
	props: {
		headingLevel: {
			type: Number,
			required: false,
			default: 1,
		},
		tripId: Number,
		tripName: String,
		tripBeginDt: String,
		tripEndDt: String,
		privacySetting: String,
		mode: {
			type: String,
			required: false,
			default: 'view', // "create", "edit", or "view"
		},
		view: {
			type: String,
			required: false,
			default: 'report', // "definition", "checklists", "people", "narrative", "comments", "report"
		},
		locale: String,
		inline: Boolean,
		list: Boolean,
		reverse: Boolean,
		isPrintLayout: Boolean,
	},
	data() {
		return {
			baseUrl: window.baseUrl,
		};
	},
	computed: {
		classes() {
			return {
				'ReportTitle--reverse': this.reverse,
				'ReportTitle--inline': this.inline,
				'ReportTitle--list': this.list,
				'ReportTitle--print': this.isPrintLayout,
			};
		},
		// Format begin date according to eBird standards
		// Truncate redundancies with end date for presentation
		beginDateFormatted() {
			// Create date objects for begin and end dates to compare
			const beginDateObj = new Date(this.tripBeginDt);
			const endDateObj = new Date(this.tripEndDt);
			// Build variables for convenient comparison to use in formatting logic below
			const sameYear = beginDateObj.getFullYear() == endDateObj.getFullYear();
			const sameMonth = sameYear && beginDateObj.getMonth() == endDateObj.getMonth();
			const sameDay = sameYear && sameMonth && beginDateObj.getDate() == endDateObj.getDate();
			// Build default formatted date (no truncation)
			const day = beginDateObj.toLocaleDateString(this.locale, { day: 'numeric' });
			const month = beginDateObj.toLocaleDateString(this.locale, { month: 'short' });
			const year = beginDateObj.toLocaleDateString(this.locale, { year: 'numeric' });
			let beginDateFormatted = `${day} ${month} ${year}`;
			// Truncate start date for display
			if (sameDay) {
				// Full begin date displays for same day trips
				beginDateFormatted = `${day} ${month} ${year}`;
			} else if (sameMonth) {
				// Only begin day appears of day month trips
				beginDateFormatted = `${day}`;
			} else if (sameYear) {
				// Begin day and month appear for same month trips
				beginDateFormatted = `${day} ${month}`;
			} else {
				// Otherwise, its different years so we display full date
				beginDateFormatted = `${day} ${month} ${year}`;
			}
			return beginDateFormatted;
		},
		// Format end date according to eBird standards
		endDateFormatted() {
			const dateObj = new Date(this.tripEndDt);
			const day = dateObj.toLocaleDateString(this.locale, { day: 'numeric' });
			const month = dateObj.toLocaleDateString(this.locale, { month: 'short' });
			const year = dateObj.toLocaleDateString(this.locale, { year: 'numeric' });
			return `${day} ${month} ${year}`;
		},
		// Calculate the number of calendar days over which the trip took place
		duration() {
			// Calculate the number of days since 1970, always rounded down to the integer day
			const beginDateInDays = this.convertDateToDay(this.tripBeginDt);
			const endDateInDays = this.convertDateToDay(this.tripEndDt);
			let daysDiff = endDateInDays - beginDateInDays;
			// If the trip took place over the same day, then daysDiff = 0 but duration should be 1,
			// If the trip took place over two days, then daysDiff = 1 but days duration should be 2
			// Therfore, we always add 1
			let duration = daysDiff + 1;
			return duration;
		},
		headingSize: function () {
			const longNameLength = 30;
			let size = this.headingLevel;
			if (!this.list && this.tripName.length > longNameLength) {
				size = size + 1;
			}
			return size;
		},
	},
	methods: {
		// Localize a date for the title tag (no eBird formatting)
		localizeDate(date) {
			const options = { dateStyle: 'long', timeStyle: 'short' };
			return new Date(date).toLocaleString(this.locale, options);
		},
		// Convert a date into days since Jan 1 1970 UTC
		// For calculating simple duration based on calendar days of trip
		convertDateToDay(date) {
			const msPerDay = 1000 * 60 * 60 * 24;
			const dateWithoutTime = date.slice(0, 10);
			const dateObj = new Date(dateWithoutTime);
			const dateInDays = dateObj / msPerDay; // days since Jan 1 1970 UTC
			return dateInDays;
		},
	},
};
</script>

<style lang="scss">
.ReportTitle {
	display: flex;
	flex-wrap: wrap;
	align-items: baseline;
	gap: $space-xs;
	color: $color-text-muted;
	.Heading {
		margin: 0;
	}
	@include breakpoint(medium) {
		gap: $space-sm;
	}
}
.ReportTitle-context {
	@include text(2);
	width: 100%;
	flex-basis: 100%;
	font-weight: bold;
	text-transform: uppercase;
}
.ReportTitle-name {
	width: 100%;
	flex-basis: 100%;
}
.ReportTitle-time {
	display: inline-flex;
	flex-wrap: wrap;
	gap: 0.25em;
	@include text(4);
	text-rendering: optimizeLegibility;
	overflow-wrap: break-word;
	@include breakpoint(medium) {
		@include text(5);
	}
}
.ReportTitle-privacy {
	// Copied from Badge
	display: inline-flex;
	align-items: center;
	justify-content: center;
	vertical-align: middle;
	color: $color-neutral-6;
	background-color: $color-primary-0;
	@include text(2);
	border-radius: 10rem;
	padding-top: 0.1rem;
	padding-bottom: 0.2rem;
	padding-left: 0.5rem;
	padding-right: 0.5rem;
}

.ReportTitle.ReportTitle--inline {
	.ReportTitle-name {
		width: auto;
		flex-basis: auto;
	}
}

.ReportTitle.ReportTitle--reverse {
	color: $color-text-muted-reverse;
	.ReportTitle-privacy {
		color: $color-neutral-1;
		background-color: $color-primary-6;
	}
}

.ReportTitle.ReportTitle--list {
	row-gap: $space-xxs;
	column-gap: $space-sm;
	@include breakpoint(medium) {
		.ReportTitle-name {
			width: auto;
			flex-basis: auto;
		}
	}
	.ReportTitle-time {
		@include text(3);
	}
}

.print {
	.ReportTitle {
		color: $color-black;
		gap: $space-md;
	}
	.ReportTitle-privacy {
		display: none;
	}
}
</style>
